import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { contentApi } from '../services/api';
import { formatDate, getErrorMessage } from '../utils/helpers';

function HomePage() {
  const { user, isAuthenticated } = useAuth();
  const [recentContent, setRecentContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRecentContent();
  }, []);

  const fetchRecentContent = async () => {
    try {
      const response = await contentApi.getContent({ 
        limit: 6,
        page: 1 
      });
      setRecentContent(response.content || []);
    } catch (err) {
      console.error('Failed to fetch recent content:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-main">
            <h1 className="hero-title">Queer Data Network</h1>
            <p className="hero-subtitle">
              Building community and sharing knowledge in the intersection of 
              LGBTQ+ identity and data/tech work.
            </p>
            <p className="hero-description">
              A space for queer folks in data science, analytics, engineering, and tech 
              to connect, learn, and support each other's professional growth.
            </p>
          </div>
          
          {isAuthenticated ? (
            <div className="welcome-back-card">
              <h2>Welcome back, {user.displayName}!</h2>
              <p>Member since {formatDate(user.createdAt)}</p>
              <div className="cta-buttons">
                {user.roles && user.roles.includes('admin') && (
                  <Link to="/content/new" className="btn btn-primary">
                    Create Resource
                  </Link>
                )}
                <Link to="/community" className="btn btn-primary">
                  Community Posts
                </Link>
                <Link to="/content" className="btn btn-secondary">
                  Browse Resources
                </Link>
              </div>
            </div>
          ) : (
            <div className="welcome-back-card">
              <h2>Join the Community</h2>
              <p>Connect with queer folks in data and tech</p>
              <div className="cta-buttons">
                <Link to="/login" className="btn btn-primary">
                  Sign In
                </Link>
                <Link to="/register" className="btn btn-secondary">
                  Join QDN
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Recent Content Section */}
      <section className="recent-content-section">
        <div className="container">
          <div className="section-header">
            <h2>Latest Resources</h2>
            <Link to="/content" className="view-all-link">View All →</Link>
          </div>
          
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading recent content...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>Unable to load recent content</p>
            </div>
          ) : recentContent.length === 0 ? (
            <div className="empty-state">
              <p>No content available yet.</p>
              {isAuthenticated && (
                <Link to="/content/new" className="btn btn-primary">
                  Be the first to create content!
                </Link>
              )}
            </div>
          ) : (
            <div className="content-preview-grid">
              {recentContent.slice(0, 6).map((item) => (
                <article key={item.id || item._id} className="content-preview-card">
                  <div className="preview-header">
                    <span className={`content-type-badge content-type-${item.type}`}>
                      {item.type}
                    </span>
                  </div>
                  
                  <h3 className="preview-title">
                    <Link to={`/content/${item.slug || item.id}`}>
                      {item.title}
                    </Link>
                  </h3>
                  
                  {item.excerpt && (
                    <p className="preview-excerpt">
                      {item.excerpt.substring(0, 120)}...
                    </p>
                  )}
                  
                  <div className="preview-meta">
                    <span className="preview-author">by {item.authorDisplayName}</span>
                    <span className="meta-separator">•</span>
                    <span className="preview-date">{formatDate(item.publishedAt)}</span>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Mission Statement Section */}
      <section className="mission-section">
        <div className="container">
          <div className="mission-content">
            <h2>Our Mission</h2>
            <p>
              The Queer Data Network exists to create visibility, community, and professional 
              support for LGBTQ+ individuals working in data science, analytics, engineering, 
              and related technical fields.
            </p>
            <p>
              We believe that diverse perspectives strengthen both our technical work and 
              our communities. Through shared resources, discussions, and mutual support, 
              we're building a more inclusive future in tech.
            </p>
          </div>
        </div>
      </section>

      {/* Community Stats & CTA */}
      <section className="community-section">
        <div className="container">
          <div className="community-stats">
            <div className="stat-card">
              <h3>Resources</h3>
              <p className="stat-number">{recentContent.length}</p>
              <p className="stat-label">Articles & Guides</p>
            </div>
            <div className="stat-card">
              <h3>Growing Community</h3>
              <p className="stat-number">∞</p>
              <p className="stat-label">Potential</p>
            </div>
            <div className="stat-card">
              <h3>Open Source</h3>
              <p className="stat-number">100%</p>
              <p className="stat-label">Transparency</p>
            </div>
          </div>
          
          {!isAuthenticated && (
            <div className="final-cta">
              <h3>Ready to get involved?</h3>
              <Link to="/register" className="btn btn-primary btn-lg">
                Join the Network
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default HomePage;